import React, {
  RefObject,
  useEffect,
} from 'react';
import {
  useUserSettings,
} from 'core/hooks';
import FilterForm from 'core/Filters/FilterForm';
import FilterWrapper from 'core/Filters/FilterWrapper';
import {
  FieldValues,
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  DateRangeFilterProps,
  FilterInstance,
  FilterFieldClassNameRule,
  FilterObjectType,
} from 'core/Filters/types';
import {
  useUpsertUserSettings,
} from 'pages/Dashboard/hooks/useUserSettings';
import {
  filterComponentsByType,
} from 'core/Filters/attributes';
import {
  getFilterDefaultValues,
} from 'core/Filters/util';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import useFilterVersion from 'core/Filters/hooks';
import defer from 'lodash/defer';

type Props = {
  pageObject: FilterObjectType;
  onFilterUpdate?: () => void;
  instanceRef?: RefObject<FilterInstance | null | undefined>;
  useStorage?: boolean;
  fieldClassNameRules?: FilterFieldClassNameRule[];
  dateRangeProps?: Record<string, DateRangeFilterProps>;
};

export default function Filters({
  pageObject,
  onFilterUpdate,
  instanceRef,
  useStorage = false,
  fieldClassNameRules,
  dateRangeProps,
}: Props) {
  const {
    settings,
    setSettings,
    loadingUserSettings,
  } = useUserSettings();

  const isLocalSettingsInvalid = useFilterVersion(pageObject);
  const components = filterComponentsByType(pageObject);

  const defaultValues = getFilterDefaultValues(
    settings,
    pageObject,
    components,
    useStorage && !isLocalSettingsInvalid
      ? JSON.parse(sessionStorage.getItem(`${pageObject}Filters`) ?? 'null')
      : null,
  );
  const formProps = useForm<FieldValues>({
    mode: 'all',
    defaultValues,
  });

  const { getValues, reset } = formProps;

  useEffect(() => {
    const filterValues = getFilterDefaultValues(
      settings,
      pageObject,
      components,
    );
    const shouldReset = useStorage && isLocalSettingsInvalid && !loadingUserSettings;
    if (shouldReset) {
      const hasFilterValues = !isNil(filterValues) && !isEmpty(filterValues);
      if (hasFilterValues) {
        reset(filterValues as FieldValues);
      } else {
        instanceRef?.current?.clearAll();
      }
      defer(() => { onFilterUpdate?.(); });
    }
  }, [useStorage, isLocalSettingsInvalid, loadingUserSettings]);

  const {
    mutateAsync: updateSettings,
    isPending: isLoading,
  } = useUpsertUserSettings();

  const clearAll = () => {
    instanceRef?.current?.clearAll();
  };

  const applyFilter = () => {
    onFilterUpdate?.();
  };

  const saveAndApplyFilter = async () => {
    const preferenceJson = JSON.stringify({
      ...settings,
      [`${pageObject}Filters`]: getValues(),
    });

    const response = await updateSettings({ preferenceJson });
    setSettings(JSON.parse(response?.preferenceJson ?? '{}'));

    applyFilter();
  };

  return (
    <FormProvider {...formProps}>
      <FilterWrapper
        clearAll={clearAll}
        applyFilter={saveAndApplyFilter}
        label="Filters"
        isLoading={isLoading}
        closeText="Apply"
        onClose={applyFilter}
      >
        <FilterForm
          components={components}
          defaultValues={defaultValues}
          instanceRef={instanceRef}
          fieldClassNameRules={fieldClassNameRules}
          dateRangeProps={dateRangeProps}
        />
      </FilterWrapper>
    </FormProvider>
  );
}
